import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — The Fundamental Counting Principle
// ─────────────────────────────────────────────────────────────────
function DiagramCountingPrinciple() {
  const W = 900, H = 340;

  // Tree diagram: choosing shirt (3) × pants (2) × shoes (2) = 12 outfits
  const shirts  = ['Red','Blue','Green'];
  const pants   = ['Black','Grey'];
  const shoes   = ['Sneakers','Boots'];

  const shirtColors  = ['#c0392b','#2E75B6','#27ae60'];
  const pantsColors  = ['#1F3864','#888'];
  const shoesColors  = ['#e07b00','#9b59b6'];

  // Layout
  const rootX = 60, rootY = 170;
  const col1X = 200, col2X = 390, col3X = 580;
  const leafX = 780;

  const col1Ys = [80, 170, 260];
  const col2Ys = [60, 115, 175, 230, 285, 340]; // adjusted below
  const totalOutfits = shirts.length * pants.length * shoes.length;

  // Generate all leaf positions
  const leaves = [];
  shirts.forEach((sh, si) => {
    pants.forEach((pa, pi) => {
      shoes.forEach((sho, shi) => {
        const idx = si * pants.length * shoes.length + pi * shoes.length + shi;
        leaves.push({
          sh, pa, sho, si, pi, shi: shi,
          y: 26 + idx * ((H - 40) / totalOutfits),
          shColor: shirtColors[si],
          paColor: pantsColors[pi],
          shoColor: shoesColors[shi],
        });
      });
    });
  });

  // Col2 node positions (one per shirt×pants combo)
  const col2Nodes = [];
  shirts.forEach((sh, si) => {
    pants.forEach((pa, pi) => {
      const leafsForThis = leaves.filter(l => l.si === si && l.pi === pi);
      const midY = leafsForThis.reduce((a, l) => a + l.y, 0) / leafsForThis.length;
      col2Nodes.push({ sh, pa, si, pi, y: midY });
    });
  });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Fundamental Counting Principle — 3 Shirts × 2 Pants × 2 Shoes = 12 Outfits</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Panel background */}
        <rect x={10} y={10} width={W-20} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>

        {/* Title labels for each level */}
        {[
          { x: rootX,  label: 'Start',   sub: '' },
          { x: col1X,  label: 'Shirt',   sub: '(3 choices)' },
          { x: col2X,  label: 'Pants',   sub: '(2 choices)' },
          { x: col3X,  label: 'Shoes',   sub: '(2 choices)' },
          { x: leafX,  label: 'Outfit',  sub: `(${totalOutfits} total)` },
        ].map(({ x, label, sub }) => (
          <g key={x}>
            <text x={x} y={H-18} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">{label}</text>
            <text x={x} y={H-6}  textAnchor="middle" fontSize="10" fill="#888">{sub}</text>
          </g>
        ))}

        {/* Root node */}
        <circle cx={rootX} cy={rootY} r={14} fill="#1F3864" stroke="#fff" strokeWidth="2"/>
        <text x={rootX} y={rootY+4} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">GO</text>

        {/* Lines: root → shirt nodes */}
        {col1Ys.map((y, i) => (
          <line key={i} x1={rootX+14} y1={rootY} x2={col1X-18} y2={y} stroke={shirtColors[i]} strokeWidth="1.8" opacity="0.7"/>
        ))}

        {/* Shirt nodes */}
        {shirts.map((sh, i) => (
          <g key={i}>
            <circle cx={col1X} cy={col1Ys[i]} r={18} fill={shirtColors[i]} stroke="#fff" strokeWidth="2"/>
            <text x={col1X} y={col1Ys[i]+4} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">{sh}</text>
          </g>
        ))}

        {/* Lines: shirt → pants nodes */}
        {col2Nodes.map(({ si, pi, y }) => (
          <line key={`${si}-${pi}`}
            x1={col1X+18} y1={col1Ys[si]} x2={col2X-18} y2={y}
            stroke={pantsColors[pi]} strokeWidth="1.5" opacity="0.7"/>
        ))}

        {/* Pants nodes */}
        {col2Nodes.map(({ pa, pi, y }, i) => (
          <g key={i}>
            <circle cx={col2X} cy={y} r={16} fill={pantsColors[pi]} stroke="#fff" strokeWidth="2"/>
            <text x={col2X} y={y+4} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">{pa}</text>
          </g>
        ))}

        {/* Lines: pants → shoe nodes */}
        {leaves.map((l, i) => (
          <line key={i}
            x1={col2X+16} y1={col2Nodes.find(n => n.si===l.si&&n.pi===l.pi).y}
            x2={col3X-16} y2={l.y}
            stroke={shoesColors[l.shi]} strokeWidth="1.4" opacity="0.6"/>
        ))}

        {/* Shoe nodes */}
        {leaves.map((l, i) => (
          <g key={i}>
            <circle cx={col3X} cy={l.y} r={13} fill={shoesColors[l.shi]} stroke="#fff" strokeWidth="1.5"/>
            <text x={col3X} y={l.y+3} textAnchor="middle" fontSize="8" fill="#fff" fontWeight="700">
              {l.sho === 'Sneakers' ? 'Snkr' : 'Boot'}
            </text>
          </g>
        ))}

        {/* Lines to leaf labels */}
        {leaves.map((l, i) => (
          <line key={i} x1={col3X+13} y1={l.y} x2={leafX-10} y2={l.y}
            stroke="#ccd9ea" strokeWidth="1"/>
        ))}

        {/* Leaf labels — outfit number + abbreviated */}
        {leaves.map((l, i) => (
          <g key={i}>
            <rect x={leafX-8} y={l.y-9} width={W-leafX-22} height={18} rx={3}
              fill={`${l.shColor}22`} stroke={l.shColor} strokeWidth="0.8"/>
            <text x={leafX+2} y={l.y+4} fontSize="9" fill="#1F3864">
              {`${i+1}. ${l.sh}+${l.pa}+${l.sho}`}
            </text>
          </g>
        ))}

        {/* Multiplication principle callout */}
        <rect x={20} y={14} width={340} height={42} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={190} y={30} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Multiplication Principle:</text>
        <text x={190} y={48} textAnchor="middle" fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">3 × 2 × 2 = 12 total outcomes</text>
      </svg>
      <div style={S.dCaption}>
        The Fundamental Counting Principle: if one task can be done in m ways and a second independent task in n ways, both together can be done in m×n ways. This extends to any number of stages. The tree diagram shows every branch explicitly — each path from root to leaf is one complete outfit. Multiplying choices at each stage gives the total without listing every possibility.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Factorials Visualised
// ─────────────────────────────────────────────────────────────────
function DiagramFactorials() {
  const W = 900, H = 330;

  // Show all 3! = 6 arrangements of A, B, C as slot diagrams
  const perms3 = [
    ['A','B','C'],['A','C','B'],
    ['B','A','C'],['B','C','A'],
    ['C','A','B'],['C','B','A'],
  ];
  const colors = { A:'#2E75B6', B:'#c0392b', C:'#27ae60' };
  const slotW = 34, slotH = 34, slotGap = 6;

  // Factorial bar chart
  const facts = [1,1,2,6,24,120,720];
  const barOx = 520, barBase = 270, barMaxH = 200, barW2 = 38, barGap2 = 10;
  const maxFact = 720;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Factorials — All 3! = 6 Arrangements of {'{A, B, C}'} and Growth of n!</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: 6 arrangements ── */}
        <rect x={10} y={10} width={490} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={255} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          All 3! = 6 orderings of A, B, C  (3 choices × 2 remaining × 1 last)
        </text>

        {/* Slot header */}
        {['Slot 1','Slot 2','Slot 3'].map((lbl, i) => (
          <text key={i} x={50 + i*(slotW+slotGap) + slotW/2} y={52}
            textAnchor="middle" fontSize="10" fill="#888" fontWeight="700">{lbl}</text>
        ))}

        {/* 6 permutation rows */}
        {perms3.map((perm, pi) => {
          const rowY = 60 + pi * 42;
          return (
            <g key={pi}>
              {/* Row number */}
              <circle cx={28} cy={rowY + slotH/2} r={12} fill="#1F3864"/>
              <text x={28} y={rowY+slotH/2+4} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">{pi+1}</text>

              {/* Slots */}
              {perm.map((letter, li) => (
                <g key={li}>
                  <rect x={48 + li*(slotW+slotGap)} y={rowY} width={slotW} height={slotH}
                    rx={4} fill={colors[letter]} stroke="#fff" strokeWidth="2"/>
                  <text x={48 + li*(slotW+slotGap) + slotW/2} y={rowY+slotH/2+5}
                    textAnchor="middle" fontSize="16" fill="#fff" fontWeight="700">{letter}</text>
                </g>
              ))}

              {/* Arrow label */}
              <text x={178} y={rowY+slotH/2+5} fontSize="11" fill="#555">→  Arrangement #{pi+1}</text>
            </g>
          );
        })}

        {/* Counting breakdown */}
        <rect x={18} y={H-56} width={464} height={40} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={250} y={H-40} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          3! = 3 × 2 × 1 = 6   (slot 1: 3 choices → slot 2: 2 left → slot 3: 1 left)
        </text>
        <text x={250} y={H-24} textAnchor="middle" fontSize="11" fill="#555">
          n! = n × (n−1) × (n−2) × … × 2 × 1    |    0! = 1  by convention
        </text>

        {/* ── RIGHT: factorial growth bar chart ── */}
        <rect x={510} y={10} width={W-524} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={510+(W-524)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Factorial Growth  n!</text>

        {/* Bars */}
        {facts.map((f, i) => {
          const bh = Math.max(2, (f / maxFact) * barMaxH);
          const bx = barOx + i * (barW2 + barGap2);
          const alpha = 0.3 + (f/maxFact)*0.7;
          return (
            <g key={i}>
              <rect x={bx} y={barBase-bh} width={barW2} height={bh}
                fill="#2E75B6" opacity={alpha} rx={3}/>
              <text x={bx+barW2/2} y={barBase+14} textAnchor="middle" fontSize="11" fill="#888">{i}!</text>
              <text x={bx+barW2/2} y={barBase-bh-5} textAnchor="middle" fontSize={f>99?'9':'10'}
                fill="#2E75B6" fontWeight="700">{f >= 100 ? f.toLocaleString() : f}</text>
            </g>
          );
        })}

        {/* Axis */}
        <line x1={barOx-5} y1={barBase} x2={barOx + facts.length*(barW2+barGap2)} y2={barBase}
          stroke="#bbb" strokeWidth="1.5"/>

        {/* Explosion annotation */}
        <rect x={barOx+4*(barW2+barGap2)+2} y={barBase-barMaxH-30} width={200} height={36} rx={3}
          fill="#fff7ee" stroke="#e07b00" strokeWidth="1.2"/>
        <text x={barOx+4*(barW2+barGap2)+102} y={barBase-barMaxH-16} textAnchor="middle"
          fontSize="11" fill="#e07b00" fontWeight="700">Factorials grow</text>
        <text x={barOx+4*(barW2+barGap2)+102} y={barBase-barMaxH-3} textAnchor="middle"
          fontSize="11" fill="#e07b00">faster than exponentials!</text>

        {/* Notable values */}
        {[
          {n:10, val:'3,628,800'},{n:20, val:'≈2.4×10¹⁸'},{n:52, val:'≈8×10⁶⁷ (deck of cards)'}
        ].map(({n,val},i) => (
          <g key={i}>
            <rect x={barOx-5} y={barBase+30+i*22} width={W-barOx-30} height={19} rx={2}
              fill={i%2===0?'#f8fbff':'#fff'} stroke="#eee" strokeWidth="0.5"/>
            <text x={barOx+2} y={barBase+43+i*22} fontSize="10" fill="#1F3864"
              fontFamily="'Courier New',monospace" fontWeight="700">{n}! =</text>
            <text x={barOx+32} y={barBase+43+i*22} fontSize="10" fill="#2E75B6"
              fontFamily="'Courier New',monospace">{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        n! counts the number of ways to arrange n distinct objects in a row. For 3 items there are 6 arrangements; for 10 items over 3.6 million; for a standard 52-card deck, more arrangements than there are atoms in the observable universe. Factorials grow superexponentially — 0! = 1 by convention because there is exactly one way to arrange zero items.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Permutations P(n,r)
// ─────────────────────────────────────────────────────────────────
function DiagramPermutations() {
  const W = 900, H = 350;

  // Show P(5,3) = 60: arranging 3 from {A,B,C,D,E}
  const items = ['A','B','C','D','E'];
  const itemColors = ['#2E75B6','#c0392b','#27ae60','#9b59b6','#e07b00'];
  const r = 3, n = 5;
  const fact = k => k<=1?1:k*fact(k-1);
  const Pnr = fact(n)/fact(n-r); // = 60

  // Show the slot-filling logic visually
  const slots = [
    { choices: n,   label: '5 choices', color: '#2E75B6' },
    { choices: n-1, label: '4 remain',  color: '#c0392b' },
    { choices: n-2, label: '3 remain',  color: '#27ae60' },
  ];

  // Show a sample of 12 arrangements
  const samplePerms = [
    ['A','B','C'],['A','B','D'],['A','C','B'],['A','D','E'],
    ['B','A','C'],['B','C','D'],['C','A','B'],['C','D','E'],
    ['D','A','B'],['D','B','E'],['E','A','C'],['E','C','D'],
  ];

  // Right panel
  const panX = 530;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Permutations P(n,r) — Ordered Selection of r Items from n Distinct Items</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT ── */}
        <rect x={10} y={10} width={510} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={265} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          P(5, 3) — choosing 3 from {'{A,B,C,D,E}'} where ORDER matters
        </text>

        {/* Source pool */}
        <text x={22} y={54} fontSize="12" fill="#1F3864" fontWeight="700">Available items:</text>
        {items.map((item, i) => (
          <g key={i}>
            <circle cx={140 + i*50} cy={48} r={18} fill={itemColors[i]} stroke="#fff" strokeWidth="2"/>
            <text x={140 + i*50} y={53} textAnchor="middle" fontSize="15" fill="#fff" fontWeight="700">{item}</text>
          </g>
        ))}

        {/* Slot-filling visualisation */}
        <text x={22} y={86} fontSize="12" fill="#1F3864" fontWeight="700">Fill 3 ordered slots:</text>
        {slots.map(({ choices, label, color }, i) => {
          const sx = 22 + i * 155;
          return (
            <g key={i}>
              <rect x={sx} y={94} width={140} height={58} rx={5} fill={`${color}22`} stroke={color} strokeWidth="2"/>
              <text x={sx+70} y={116} textAnchor="middle" fontSize="22" fill={color} fontWeight="700">
                {choices}
              </text>
              <text x={sx+70} y={132} textAnchor="middle" fontSize="10" fill={color}>{label}</text>
              <text x={sx+70} y={145} textAnchor="middle" fontSize="10" fill="#888">
                Slot {i+1}
              </text>
              {i < slots.length-1 && (
                <text x={sx+150} y={130} textAnchor="middle" fontSize="20" fill="#1F3864" fontWeight="700">×</text>
              )}
            </g>
          );
        })}

        {/* Result */}
        <rect x={22} y={162} width={465} height={36} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.3"/>
        <text x={255} y={178} textAnchor="middle" fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          5 × 4 × 3 = 60 = P(5,3) = 5! / (5−3)! = 120/2
        </text>
        <text x={255} y={192} textAnchor="middle" fontSize="11" fill="#555">
          P(n,r) = n! / (n−r)!  — order matters, no repetition
        </text>

        {/* Sample permutations grid */}
        <text x={22} y={218} fontSize="12" fill="#1F3864" fontWeight="700">
          Sample of 12 (out of 60) arrangements:
        </text>
        {samplePerms.map((perm, pi) => {
          const col = pi % 4, row = Math.floor(pi / 4);
          const gx = 22 + col * 120, gy = 224 + row * 38;
          return (
            <g key={pi}>
              <rect x={gx} y={gy} width={112} height={30} rx={3}
                fill={pi%2===0?'#f8fbff':'#EBF3FB'} stroke="#ccd9ea" strokeWidth="0.8"/>
              {perm.map((letter, li) => (
                <g key={li}>
                  <rect x={gx+6+li*33} y={gy+4} width={28} height={22} rx={3}
                    fill={itemColors[items.indexOf(letter)]} opacity="0.85"/>
                  <text x={gx+6+li*33+14} y={gy+19} textAnchor="middle"
                    fontSize="12" fill="#fff" fontWeight="700">{letter}</text>
                </g>
              ))}
            </g>
          );
        })}
        <text x={265} y={H-18} textAnchor="middle" fontSize="10" fill="#888" fontStyle="italic">
          …and 48 more — ABC and ACB are different because order matters
        </text>

        {/* ── RIGHT: formula panel ── */}
        <rect x={panX} y={10} width={W-panX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Permutation Formulas</text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6',
            title:'Standard P(n, r)',
            lines:['P(n,r) = n! / (n−r)!', 'Ordered selection of r', 'from n distinct items', 'No repetition allowed'] },
          { y:136, bg:'#fff7ee', bc:'#e07b00',
            title:'With Repetition',
            lines:['nʳ  arrangements', 'Each slot can hold any', 'of the n items again', 'e.g. PIN codes, passwords'] },
          { y:236, bg:'#f0fff4', bc:'#27ae60',
            title:'All n Items  (r = n)',
            lines:['P(n,n) = n!', 'Total arrangements', 'of all n objects', 'e.g. anagram count'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={W-panX-30} height={94} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={panX+18} y={y+32+j*17} fontSize="11"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A permutation is an ordered arrangement — ABC and BAC are counted separately. P(n,r) fills r slots one by one: n choices for slot 1, n−1 for slot 2, …, n−r+1 for slot r, giving n×(n−1)×…×(n−r+1) = n!/(n−r)!. When repetition is allowed (like a PIN), each slot independently has n choices, giving nʳ arrangements instead.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Combinations C(n,r)
// ─────────────────────────────────────────────────────────────────
function DiagramCombinations() {
  const W = 900, H = 350;

  // C(5,3) = 10: choosing 3 from {A,B,C,D,E} ignoring order
  const items = ['A','B','C','D','E'];
  const itemColors = ['#2E75B6','#c0392b','#27ae60','#9b59b6','#e07b00'];

  const fact = k => k<=1?1:k*fact(k-1);
  const C = (n,r) => fact(n)/(fact(r)*fact(n-r));
  const allCombos = [];
  for (let i=0;i<5;i++) for (let j=i+1;j<5;j++) for (let k=j+1;k<5;k++)
    allCombos.push([items[i],items[j],items[k]]);

  // Show the "divide out duplicates" concept: show ABC with its 3!=6 permutations
  const ABCperms = [['A','B','C'],['A','C','B'],['B','A','C'],['B','C','A'],['C','A','B'],['C','B','A']];

  const panX = 520;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Combinations C(n,r) — Unordered Selection of r Items from n (Order Does NOT Matter)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT ── */}
        <rect x={10} y={10} width={500} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={255} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          C(5,3) = 10 — all {C(5,3)} subsets of size 3 from {'{A,B,C,D,E}'}
        </text>

        {/* All 10 combinations in a 2×5 grid */}
        {allCombos.map((combo, ci) => {
          const col = ci % 5, row = Math.floor(ci / 5);
          const gx = 16 + col * 96, gy = 42 + row * 46;
          return (
            <g key={ci}>
              <rect x={gx} y={gy} width={88} height={38} rx={4}
                fill={ci%2===0?'#f0fff4':'#EBF3FB'} stroke="#ccd9ea" strokeWidth="1"/>
              {combo.map((letter, li) => (
                <g key={li}>
                  <circle cx={gx+16+li*26} cy={gy+19} r={11}
                    fill={itemColors[items.indexOf(letter)]} stroke="#fff" strokeWidth="1.5"/>
                  <text x={gx+16+li*26} y={gy+24} textAnchor="middle"
                    fontSize="11" fill="#fff" fontWeight="700">{letter}</text>
                </g>
              ))}
            </g>
          );
        })}

        {/* Divider */}
        <line x1={16} y1={140} x2={496} y2={140} stroke="#eee" strokeWidth="1"/>

        {/* Key insight: P→C by dividing out r! */}
        <text x={22} y={160} fontSize="12" fill="#1F3864" fontWeight="700">
          Why divide by r! = 3! = 6?
        </text>
        <text x={22} y={175} fontSize="11" fill="#555">
          These 6 permutations of A,B,C all represent the SAME combination {'{A,B,C}'}:
        </text>

        {ABCperms.map((perm, pi) => {
          const gx = 16 + pi * 78, gy = 182;
          const isSame = true;
          return (
            <g key={pi}>
              <rect x={gx} y={gy} width={72} height={32} rx={3}
                fill="#fff7ee" stroke="#e07b00" strokeWidth="1.2"/>
              {perm.map((letter, li) => (
                <rect key={li} x={gx+4+li*21} y={gy+4} width={18} height={24} rx={2}
                  fill={itemColors[items.indexOf(letter)]} opacity="0.8"/>
              ))}
              {perm.map((letter, li) => (
                <text key={li} x={gx+4+li*21+9} y={gy+20} textAnchor="middle"
                  fontSize="10" fill="#fff" fontWeight="700">{letter}</text>
              ))}
            </g>
          );
        })}
        <text x={22} y={230} fontSize="11" fill="#e07b00" fontWeight="700">
          ↑  All 6 are counted in P(5,3)=60 but only 1 is counted in C(5,3)=10
        </text>

        {/* Formula derivation */}
        <rect x={16} y={240} width={480} height={56} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.3"/>
        <text x={256} y={258} textAnchor="middle" fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          C(n,r) = P(n,r) / r! = n! / [r!(n−r)!]
        </text>
        <text x={256} y={274} textAnchor="middle" fontSize="11" fill="#1F3864">
          C(5,3) = 60 / 6 = 10   (divide out the r! duplicate orderings)
        </text>
        <text x={256} y={288} textAnchor="middle" fontSize="11" fill="#555">
          Also written as  ⁿCᵣ  or  "n choose r"  or  (n r)  (binomial coefficient)
        </text>

        {/* ── RIGHT: comparison table ── */}
        <rect x={panX} y={10} width={W-panX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">P vs C Comparison</text>

        {/* Header */}
        {['n','r','P(n,r)','C(n,r)'].map((h, i) => (
          <g key={i}>
            <rect x={panX+8+i*88} y={36} width={86} height={22} rx={2}
              fill={i<2?'#1F3864':i===2?'#c0392b':'#27ae60'}/>
            <text x={panX+51+i*88} y={51} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{h}</text>
          </g>
        ))}

        {/* Comparison rows */}
        {[
          [5,1,5,5],[5,2,20,10],[5,3,60,10],[5,4,120,5],[5,5,120,1],
          [6,2,30,15],[6,3,120,20],[10,2,90,45],[10,3,720,120],
        ].map(([n2,r2,p,c], i) => (
          <g key={i}>
            {[n2,r2,p,c].map((val, j) => (
              <g key={j}>
                <rect x={panX+8+j*88} y={60+i*28} width={86} height={26}
                  fill={i%2===0?'#f8fbff':'#fff'} stroke="#eee" strokeWidth="0.5"/>
                <text x={panX+51+j*88} y={60+i*28+17} textAnchor="middle" fontSize="12"
                  fill={j===2?'#c0392b':j===3?'#27ae60':'#333'}
                  fontFamily="'Courier New',monospace" fontWeight={j>=2?'700':'400'}>{val}</text>
              </g>
            ))}
          </g>
        ))}

        {/* Note */}
        <rect x={panX+8} y={H-46} width={W-panX-30} height={30} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={panX+(W-panX-14)/2} y={H-31} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">
          P(n,r) ÷ C(n,r) = r!  always
        </text>
        <text x={panX+(W-panX-14)/2} y={H-18} textAnchor="middle" fontSize="10" fill="#555">
          Permutations always ≥ Combinations
        </text>
      </svg>
      <div style={S.dCaption}>
        A combination is an unordered selection — {'{A,B,C}'} and {'{C,B,A}'} are the same combination. Starting from P(n,r) ordered arrangements, we divide by r! (the number of ways to reorder r items) because every combination appears r! times in the permutation count. C(5,3) = 60/6 = 10. The table shows how rapidly P grows compared to C as r increases.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Pascal's Triangle and Binomial Coefficients
// ─────────────────────────────────────────────────────────────────
function DiagramPascal() {
  const W = 900, H = 340;

  const fact = k => { let f=1; for(let i=2;i<=k;i++) f*=i; return f; };
  const C = (n,r) => r<0||r>n?0:fact(n)/(fact(r)*fact(n-r));

  // Build Pascal's triangle rows 0-7
  const rows = Array.from({length:8}, (_,n) =>
    Array.from({length:n+1}, (_,r) => C(n,r))
  );

  // Layout: triangle on left, properties on right
  const triCx = 240, triTop = 30, rowH = 36, nodeR = 18;
  const panX = 510;

  // Color scale by value
  const maxVal = 35;
  const nodeColor = v => {
    if (v===1) return '#1F3864';
    if (v<=4)  return '#2E75B6';
    if (v<=10) return '#27ae60';
    if (v<=20) return '#e07b00';
    return '#c0392b';
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Pascal's Triangle — C(n,r) Arranged as a Triangle, Row n, Column r</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: Pascal's triangle ── */}
        <rect x={10} y={10} width={490} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={255} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Each entry = C(n,r) = sum of two entries directly above
        </text>

        {/* Row labels */}
        {rows.map((row, n) => (
          <text key={n} x={22} y={triTop + n*rowH + nodeR/2 + 4}
            fontSize="10" fill="#aaa" textAnchor="middle">n={n}</text>
        ))}

        {/* Nodes */}
        {rows.map((row, n) =>
          row.map((val, r) => {
            const cx = triCx + (r - n/2) * (nodeR*2 + 6);
            const cy = triTop + n * rowH + nodeR;
            // Lines to children
            const childLines = [];
            if (n < rows.length - 1) {
              const cy2 = triTop + (n+1)*rowH + nodeR;
              const cx2L = triCx + (r - (n+1)/2) * (nodeR*2+6);
              const cx2R = triCx + (r+1 - (n+1)/2) * (nodeR*2+6);
              childLines.push(
                <line key="L" x1={cx} y1={cy+nodeR} x2={cx2L} y2={cy2-nodeR}
                  stroke="#ddd" strokeWidth="1.2"/>,
                <line key="R" x1={cx} y1={cy+nodeR} x2={cx2R} y2={cy2-nodeR}
                  stroke="#ddd" strokeWidth="1.2"/>
              );
            }
            return (
              <g key={`${n}-${r}`}>
                {childLines}
                <circle cx={cx} cy={cy} r={nodeR} fill={nodeColor(val)} stroke="#fff" strokeWidth="1.5"/>
                <text x={cx} y={cy+4} textAnchor="middle" fontSize={val>9?'10':'12'}
                  fill="#fff" fontWeight="700">{val}</text>
              </g>
            );
          })
        )}

        {/* Row sum annotations on right side of triangle */}
        {rows.map((row, n) => {
          const rowSum = row.reduce((a,b)=>a+b,0);
          const lastCx = triCx + (n/2) * (nodeR*2+6);
          return (
            <g key={n}>
              <text x={lastCx + nodeR + 28} y={triTop + n*rowH + nodeR + 4}
                fontSize="10" fill="#27ae60" fontFamily="'Courier New',monospace">
                = 2ⁿ = {rowSum}
              </text>
            </g>
          );
        })}

        {/* ── RIGHT: properties panel ── */}
        <rect x={panX} y={10} width={W-panX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Key Properties</text>

        {[
          { y:34,  bg:'#EBF3FB', bc:'#2E75B6',
            title:'Construction rule',
            lines:['C(n,r) = C(n−1,r−1) + C(n−1,r)','Each entry = sum of the','two entries directly above.','Edges are always 1.'] },
          { y:124, bg:'#f0fff4', bc:'#27ae60',
            title:'Row sums',
            lines:['Row n sums to 2ⁿ','Σᵣ C(n,r) = 2ⁿ','Counts all subsets of an','n-element set.'] },
          { y:214, bg:'#fff7ee', bc:'#e07b00',
            title:'Symmetry',
            lines:['C(n,r) = C(n, n−r)','Choosing r items is the','same as excluding n−r.','Rows are palindromes.'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={W-panX-30} height={84} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="11"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}

        {/* Binomial theorem note */}
        <rect x={panX+8} y={H-56} width={W-panX-30} height={40} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={panX+(W-panX-14)/2} y={H-41} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">
          Binomial Theorem:
        </text>
        <text x={panX+(W-panX-14)/2} y={H-24} textAnchor="middle" fontSize="11" fill="#1F3864"
          fontFamily="'Courier New',monospace">
          (a+b)ⁿ = Σ C(n,r) aⁿ⁻ʳ bʳ
        </text>
      </svg>
      <div style={S.dCaption}>
        Pascal's Triangle arranges binomial coefficients C(n,r) in rows — row n holds all C(n,0), C(n,1), …, C(n,n). Each interior entry is the sum of the two above it. Row sums double every row (they equal 2ⁿ, counting all subsets). The triangle is symmetric because choosing r items is equivalent to leaving out n−r. Rows of Pascal's Triangle are exactly the coefficients in binomial expansions (a+b)ⁿ.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Applications and The Decision Framework
// ─────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 350;

  const fact = k => { let f=1; for(let i=2;i<=k;i++) f*=i; return f; };
  const P = (n,r) => fact(n)/fact(n-r);
  const C = (n,r) => fact(n)/(fact(r)*fact(n-r));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Counting Framework — Deciding Which Formula to Use</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Header */}
        <rect x={14} y={10} width={W-28} height={38} rx={5} fill="#1F3864"/>
        <text x={W/2} y={27} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">
          Two key questions determine the formula: Does order matter? Is repetition allowed?
        </text>
        <text x={W/2} y={42} textAnchor="middle" fontSize="11" fill="#aac8e8">
          Answer both questions about the problem before writing any formula
        </text>

        {/* 2×2 Grid of formula cases */}
        {[
          {
            row:0, col:0, color:'#c0392b', fill:'#fff0f0',
            header:'Order matters, No repetition',
            formula:'P(n,r) = n! / (n−r)!',
            example:'n=5, r=3  →  P(5,3) = 60',
            apps:['Race results (1st/2nd/3rd)','Arranging books on a shelf','Assigning distinct roles (Pres,VP,Sec)','Creating a unique password (no repeat chars)'],
          },
          {
            row:0, col:1, color:'#2E75B6', fill:'#EBF3FB',
            header:'Order matters, Repetition allowed',
            formula:'nʳ',
            example:'n=5, r=3  →  5³ = 125',
            apps:['PIN codes and passwords','Rolling dice multiple times','Combinations lock (digits repeat)','Telephone numbers'],
          },
          {
            row:1, col:0, color:'#27ae60', fill:'#f0fff4',
            header:'Order does NOT matter, No repetition',
            formula:'C(n,r) = n! / [r!(n−r)!]',
            example:'n=5, r=3  →  C(5,3) = 10',
            apps:['Lottery: pick 6 from 49','Choosing a committee of 3','Selecting items from a menu','Poker hand (5 cards from 52)'],
          },
          {
            row:1, col:1, color:'#9b59b6', fill:'#f5d0ff',
            header:'Order does NOT matter, Repetition allowed',
            formula:'C(n+r−1, r)',
            example:'n=5, r=3  →  C(7,3) = 35',
            apps:['Scoops of ice cream (flavours repeat)','Distributing identical items to bins','Choosing donuts (same flavour OK)','Stars and bars problems'],
          },
        ].map(({ row, col, color, fill, header, formula, example, apps }) => {
          const cx = 14 + col * 443, cy = 58 + row * 143;
          const cw = 434, ch = 136;
          return (
            <g key={`${row}-${col}`}>
              <rect x={cx} y={cy} width={cw} height={ch} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              {/* Header */}
              <rect x={cx+4} y={cy+4} width={cw-8} height={24} rx={3} fill={color}/>
              <text x={cx+cw/2} y={cy+20} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{header}</text>
              {/* Formula */}
              <rect x={cx+8} y={cy+32} width={cw-16} height={22} rx={3} fill="#fff" stroke={color} strokeWidth="1"/>
              <text x={cx+cw/2} y={cy+47} textAnchor="middle" fontSize="13" fill={color}
                fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>
              {/* Example */}
              <text x={cx+12} y={cy+68} fontSize="11" fill="#1F3864"
                fontFamily="'Courier New',monospace" fontWeight="700">{example}</text>
              {/* Applications */}
              {apps.map((a, i) => (
                <g key={i}>
                  <circle cx={cx+18} cy={cy+80+i*14-3} r={3} fill={color}/>
                  <text x={cx+26} y={cy+80+i*14} fontSize="10" fill="#444">{a}</text>
                </g>
              ))}
            </g>
          );
        })}

        {/* Axis labels */}
        {/* Left axis: Order */}
        <text x={8} y={130} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700"
          transform="rotate(-90,8,130)">Order matters?</text>
        <text x={8} y={273} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700"
          transform="rotate(-90,8,273)">Order ignored?</text>

        {/* Bottom axis: Repetition */}
        <text x={230} y={H-6} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">No Repetition</text>
        <text x={672} y={H-6} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Repetition Allowed</text>
      </svg>
      <div style={S.dCaption}>
        The two-question framework covers all counting scenarios. "Does order matter?" separates permutations (yes) from combinations (no). "Is repetition allowed?" halves each case again. Poker hands use C(52,5) because a hand is a set — order of dealing is irrelevant. A 4-digit PIN uses 10⁴ because each digit can repeat and order defines the PIN. Identifying the correct cell before calculating prevents the most common counting errors.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function PermutationsCombinations(props) {
  const fact = k => { let f=1; for(let i=2;i<=k;i++) f*=i; return f; };
  const C = (n,r) => fact(n)/(fact(r)*fact(n-r));

  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Permutations and Combinations</h1>

      <p style={S.p}>
        Counting problems arise everywhere in statistics and probability: how many ways can a committee be formed? How many possible passwords exist? What is the probability of winning the lottery? Permutations and combinations provide the systematic tools for answering these questions without listing every possibility. The key distinction is whether the order of selection matters — arrangements where order matters are permutations; selections where only the group matters are combinations.
      </p>

      <InfoBox
        title="The Core Distinction: Does Order Matter?"
        items={[
          "Permutation: an ordered arrangement — ABC and BAC are DIFFERENT. Formula: P(n,r) = n! / (n−r)!",
          "Combination: an unordered selection — {'{A,B,C}'} and {'{C,B,A}'} are the SAME. Formula: C(n,r) = n! / [r!(n−r)!]",
          "The link: C(n,r) = P(n,r) / r! — divide permutations by r! to remove duplicate orderings",
          "Fundamental Counting Principle: if task 1 has m ways and task 2 has n ways, together they have m×n ways",
          "Factorial: n! = n×(n−1)×…×2×1 — counts all arrangements of n distinct objects; 0! = 1",
        ]}
      />

      <h2 style={S.h2}>The Fundamental Counting Principle</h2>
      <DiagramCountingPrinciple />
      <Table2 rows={[
        ["Multiplication principle", "If event A has m outcomes and event B has n outcomes,\ntogether they have m × n outcomes (when independent)"],
        ["Extension to k tasks",     "k tasks with n₁, n₂, …, nₖ options respectively:\ntotal outcomes = n₁ × n₂ × … × nₖ"],
        ["Tree diagram",             "Visual representation of all possible sequences\nBranches at each level = choices at that stage"],
        ["Addition principle",       "If events A and B are mutually exclusive:\ntotal outcomes = m + n (choose one OR the other)"],
        ["With restriction",         "Subtract the forbidden cases from the unrestricted total\nOr: count valid cases directly using modified slot counts"],
        ["Independence required",    "Multiplication principle requires each choice to be\nindependent of the others (no shared constraints)"],
      ]} />

      <h2 style={S.h2}>Factorials</h2>
      <DiagramFactorials />
      <Table2 rows={[
        ["Definition",        "n! = n × (n−1) × (n−2) × … × 2 × 1\nCounts the number of ways to arrange n distinct objects"],
        ["Base cases",        "0! = 1  (one way to arrange nothing)\n1! = 1;  2! = 2;  3! = 6;  4! = 24;  5! = 120"],
        ["Recursive form",    "n! = n × (n−1)!  — each factorial builds on the previous\nUseful for simplifying ratios: 7!/5! = 7×6 = 42"],
        ["Cancellation",      "n! / (n−k)! = n × (n−1) × … × (n−k+1)  — a falling factorial\nMost permutation/combination fractions simplify this way"],
        ["Stirling's approx.","n! ≈ √(2πn) × (n/e)ⁿ  for large n\nUseful estimate: log(n!) ≈ n·log(n) − n"],
        ["Growth rate",       "Factorials grow faster than any exponential: n! >> aⁿ for large n\n52! ≈ 8×10⁶⁷ — more than atoms in the observable universe"],
      ]} />

      <h2 style={S.h2}>Permutations</h2>
      <DiagramPermutations />
      <Table2 rows={[
        ["P(n,r) formula",    "P(n,r) = n! / (n−r)!\nOrdered selection of r items from n distinct items without repetition"],
        ["Slot-filling view",  "Slot 1: n choices; Slot 2: n−1; …; Slot r: n−r+1\nProduct = n(n−1)(n−2)…(n−r+1) = n!/(n−r)!"],
        ["All items (r=n)",   "P(n,n) = n! — arrange all n items\nNumber of anagrams of a word with distinct letters"],
        ["With repetition",   "nʳ — each of r slots independently has n choices\nPIN codes: 10⁴ = 10,000 possible 4-digit PINs"],
        ["Repeated elements", "n! / (n₁! × n₂! × … × nₖ!)  — multinomial coefficient\nAnagram of MISSISSIPPI: 11!/(4!4!2!1!) = 34,650"],
        ["Circular permutations","(n−1)! — fix one item, arrange the rest\nSeating n people at a round table: (n−1)! arrangements"],
      ]} />

      <h2 style={S.h2}>Combinations</h2>
      <DiagramCombinations />
      <Table2 rows={[
        ["C(n,r) formula",    "C(n,r) = n! / [r!(n−r)!] = P(n,r) / r!\nUnordered selection of r items from n distinct items"],
        ["'n choose r'",      "Written as ⁿCᵣ, C(n,r), or (n r)\nReads as 'n choose r' — choose r items from n"],
        ["Symmetry",          "C(n,r) = C(n, n−r) — choosing r is the same as excluding n−r\nC(52,5) = C(52,47) — pick 5 cards or leave out 47"],
        ["Boundary cases",    "C(n,0) = 1; C(n,1) = n; C(n,n) = 1\nOne way to choose nothing; n ways to choose one; one full set"],
        ["With repetition",   "C(n+r−1, r) = 'stars and bars'\nChoosing r items from n types where repeats are allowed"],
        ["Complement rule",   "C(n,r) = C(n, n−r) — use whichever is smaller to simplify\nC(100,98) = C(100,2) = 4,950 — much easier to compute"],
      ]} />

      <h2 style={S.h2}>Pascal's Triangle and Binomial Coefficients</h2>
      <DiagramPascal />
      <Table2 rows={[
        ["Pascal's identity",  "C(n,r) = C(n−1,r−1) + C(n−1,r)\nEach entry is the sum of the two directly above it"],
        ["Row sums",           "Sum of row n = 2ⁿ = total subsets of an n-element set\nΣᵣ C(n,r) = 2ⁿ"],
        ["Binomial theorem",   "(a+b)ⁿ = Σᵣ₌₀ⁿ C(n,r) · aⁿ⁻ʳ · bʳ\nCoefficients of (a+b)ⁿ are row n of Pascal's triangle"],
        ["Hockey stick identity","Σₖ₌ᵣⁿ C(k,r) = C(n+1,r+1)\nDiagonal sums in Pascal's triangle have closed forms"],
        ["Vandermonde's identity","C(m+n,r) = Σₖ C(m,k)·C(n,r−k)\nUsed in probability when combining two groups"],
        ["Negative integers",  "C(−n,r) = (−1)ʳ · C(n+r−1,r)  — generalised binomial\nUsed in generating functions and negative binomial distribution"],
      ]} />

      <h2 style={S.h2}>Applications and Decision Framework</h2>
      <DiagramApplications />
      <Table2 rows={[
        ["Lottery probability", "Pick 6 from 49: C(49,6) = 13,983,816 possible tickets\nP(win) = 1 / 13,983,816 ≈ 0.0000000715"],
        ["Poker hand",         "C(52,5) = 2,598,960 total 5-card hands\nFlush: C(4,1)×C(13,5) = 5,148; P(flush) ≈ 0.00198"],
        ["Password strength",  "8-char password with 62 chars: 62⁸ ≈ 2.18×10¹⁴\nOrder matters + repetition → multiplication principle"],
        ["Committee selection", "Choose 3 from 10 people: C(10,3) = 120\nOrder doesn't matter → combination"],
        ["Race podium",        "Top 3 finish order from 8 runners: P(8,3) = 336\nOrder matters → permutation"],
        ["Probability formula", "P(event) = (favourable outcomes) / (total equally-likely outcomes)\nCounting correctly is the foundation of classical probability"],
      ]} />

      <InfoBox
        title="Key Takeaway — The Four-Cell Decision Framework"
        items={[
          "Step 1: Does order matter? YES → permutation family.  NO → combination family.",
          "Step 2: Is repetition allowed? YES → nʳ (perm) or C(n+r−1,r) (combo).  NO → P(n,r) or C(n,r).",
          "Order matters + no repetition → P(n,r) = n!/(n−r)!  (race results, anagrams, assigning roles)",
          "Order matters + repetition → nʳ  (PIN codes, passwords, rolling dice)",
          "Order ignored + no repetition → C(n,r) = n!/[r!(n−r)!]  (committees, lottery, poker hands)",
          "Order ignored + repetition → C(n+r−1,r)  (ice cream scoops, distributing identical objects)",
          "Always simplify factorial fractions by cancellation before multiplying out large numbers.",
          "Sanity check: C(n,r) ≤ P(n,r) always; equality only when r=0 or r=1.",
        ]}
      />
    </div>
  );
}